import {Box, Typography} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import FilterTextField from '../../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import {t} from 'i18next';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import './IssueCodeDataGrid.scss';

const renderCheckboxCell = (
  params: any,
  columnKey: string,
  isChecked: boolean,
  onCellChange: (rowId: number, columnKey: string, checked: boolean) => void
) => {
  const handleChange = (checked: boolean) => {
    onCellChange(params.row.documentId, columnKey, checked);
  };
  return (
    <Box
      className={`checkbox-cell-content ${isChecked ? 'checkbox-selected' : ''}`}
    >
      <ArrCheckbox
        size="medium"
        checked={isChecked}
        onChange={handleChange}
        className="checkbox-container"
        aria-label={`${params.value}`}
      />
      <Typography
        className="typo-text body3_size job-value"
        data-testid={`issue-code-column-${columnKey}-${params.documentId}`}
        aria-hidden={true}
      >
        {params.value}
      </Typography>
    </Box>
  );
};

const HeaderCheckbox = ({
  label,
  checked,
  indeterminate,
  onChange,
  dataTestId,
}: {
  label: string;
  checked: boolean;
  indeterminate: boolean;
  onChange: (checked: boolean) => void;
  dataTestId: string;
}) => (
  <Box
    className="header-checkbox"
    data-testid={`isse-code-header-checkbox-wrapper`}
  >
    <ArrCheckbox
      size="medium"
      checked={checked}
      isIndeterminate={indeterminate}
      onChange={(checked) => onChange(checked)}
      className="checkbox-container"
      aria-label={`${label} ${t('common.arrDataGridPro.checkbox.selectAll')}`}
    />
    <Typography
      className="typo-text label2_size"
      data-testid={`issue-code-column-typography-label-${dataTestId}`}
      aria-hidden="true" // Prevent double-reading since aria-label covers it
    >
      {label}
    </Typography>
  </Box>
);

export const getColumns = (
  checkboxState: Record<string, boolean>,
  onCellChange: (rowId: number, columnKey: string, checked: boolean) => void,
  onHeaderChange: (columnKey: string, checked: boolean) => void,
  rows: any[]
): GridColDef[] => [
  {
    field: 'issueCode',
    headerName: t('ai.review.jobTypeDataGrid.issueCodeColumns.issueCode'),
    sortable: false,
    filterable: true,
    minWidth: 220,
    flex: 1,
    renderHeader: () => (
      <Typography
        tabIndex={0}
        className="typo-text label2_size"
        role="columnheader"
        data-testid={`issue-code-column-issueCode`}
      >
        {t('ai.review.jobTypeDataGrid.issueCodeColumns.issueCode')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        tabIndex={0}
        role="gridcell"
        data-testid={`issue-code-column-${params.id}-box`}
      >
        <Typography
          className="cell-content typo-text subhead5_size"
          data-testid={`issue-code-column-${params.id}`}
        >
          {params.value}
        </Typography>
      </Box>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`issueCode`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'question',
    headerName: t('ai.review.jobTypeDataGrid.issueCodeColumns.question'),
    sortable: false,
    filterable: true,
    minWidth: 630,
    flex: 1,
    renderHeader: () => (
      <Typography
        tabIndex={0}
        className="typo-text label2_size"
        role="columnheader"
        data-testid={`issue-code-column-question`}
      >
        {t('ai.review.jobTypeDataGrid.issueCodeColumns.question')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        tabIndex={0}
        role="gridcell"
        data-testid={`issue-code-column-question-${params.id}-box`}
      >
        <Typography
          className="cell-content typo-text body3_size"
          data-testid={`issue-code-column-question-${params.id}`}
        >
          {params.value}
        </Typography>
      </Box>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`question`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'relevantDocCount',
    headerName: t('ai.review.jobTypeDataGrid.issueCodeColumns.releventDocs'),
    width: 227,
    sortable: true,
    filterable: false,
    cellClassName: 'checkbox-cell',
    headerClassName: 'checkbox-header',
    renderHeader: () => {
      const keys = rows.map((r: any) => `${r.documentId}-relevantDocCount`);
      const checkedCount = keys.filter((k) => checkboxState[k]).length;
      const label = t(
        'ai.review.jobTypeDataGrid.issueCodeColumns.releventDocs'
      );
      return (
        <HeaderCheckbox
          label={label}
          checked={checkedCount === keys.length && keys.length > 0}
          indeterminate={checkedCount > 0 && checkedCount < keys.length}
          onChange={(checked) => onHeaderChange('relevantDocCount', checked)}
          dataTestId={'relevant-docs'}
        />
      );
    },
    renderCell: (params) =>
      renderCheckboxCell(
        params,
        'relevantDocCount',
        !!checkboxState[`${params.row.documentId}-relevantDocCount`],
        onCellChange
      ),
  },
  {
    field: 'nonRelevantDocCount',
    headerName: t('ai.review.jobTypeDataGrid.issueCodeColumns.notReleventDocs'),
    width: 227,
    sortable: true,
    filterable: false,
    cellClassName: 'checkbox-cell',
    headerClassName: 'checkbox-header last-column',
    renderHeader: () => {
      const keys = rows.map((r: any) => `${r.documentId}-nonRelevantDocCount`);
      const checkedCount = keys.filter((k) => checkboxState[k]).length;
      const label = t(
        'ai.review.jobTypeDataGrid.issueCodeColumns.notReleventDocs'
      );

      return (
        <HeaderCheckbox
          label={label}
          checked={checkedCount === keys.length && keys.length > 0}
          indeterminate={checkedCount > 0 && checkedCount < keys.length}
          onChange={(checked) => onHeaderChange('nonRelevantDocCount', checked)}
          dataTestId={'non-relevant-docs'}
        />
      );
    },
    renderCell: (params) =>
      renderCheckboxCell(
        params,
        'nonRelevantDocCount',
        !!checkboxState[`${params.row.documentId}-nonRelevantDocCount`],
        onCellChange
      ),
  },
];
